import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, TrendingUp, TrendingDown, ArrowRight, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Small vertical candle bar. Green if delta>0, red if <0, gray if flat.
 * The bar height is proportional to |delta| relative to the largest |delta|
 * in the given window. Includes a small marker line for the total followers value.
 */
function Candle({ delta, maxAbsDelta, isFirst }) {
  const abs = Math.abs(delta || 0);
  const pct = maxAbsDelta > 0 ? Math.max((abs / maxAbsDelta) * 100, 4) : 4;
  const color =
    isFirst || delta === 0
      ? "bg-zinc-600"
      : delta > 0
      ? "bg-emerald-500"
      : "bg-rose-500";
  return (
    <div className="flex flex-col items-center justify-end h-full">
      <div
        className={`w-2.5 rounded-sm ${color} transition-all`}
        style={{ height: `${pct}%` }}
        title={`Δ ${delta >= 0 ? "+" : ""}${delta}`}
      />
    </div>
  );
}

/** Compact grid card shown in the JGM PRO overview — one card per modelo with
 * mini candles for the last N days and a click handler for the detail modal. */
export function ModelStatsGrid({ onOpenModel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/jgmpro/models/stats-summary?days=14`, {
          withCredentials: true,
        });
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setData({ models: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        Cargando estadísticas por modelo…
      </div>
    );
  }

  const models = data?.models || [];

  if (!models.length) {
    return (
      <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
        <div className="text-5xl mb-3">📊</div>
        <p className="text-gray-400 text-sm">
          Aún no hay estadísticas por modelo. Vincula cuentas IG a modelos en la pestaña
          &quot;Cuentas&quot; y espera al primer audit diario.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="jgmpro-model-stats-grid"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      {models.map((m) => {
        const candles = m.candles || [];
        const maxAbs = Math.max(1, ...candles.map((c) => Math.abs(c.delta || 0)));
        const growth = m.delta_window || 0;
        const growthColor =
          growth > 0 ? "text-emerald-400" : growth < 0 ? "text-rose-400" : "text-gray-400";
        return (
          <button
            key={m.display_key || m.modelo_id}
            data-testid={`jgmpro-model-card-${m.modelo_id || m.display_key}`}
            onClick={() => {
              if (m.modelo_id && onOpenModel) onOpenModel(m.modelo_id, m.modelo_name);
            }}
            disabled={!m.modelo_id}
            className={`text-left bg-zinc-900/60 border border-white/10 rounded-2xl p-4 transition-all ${
              m.modelo_id ? "hover:border-pink-500/40 cursor-pointer" : "cursor-default opacity-90"
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              {m.photo_url ? (
                <img
                  src={m.photo_url}
                  alt={m.modelo_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-lg">
                  👤
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm truncate">
                  {m.modelo_name}
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {m.latest_followers.toLocaleString()} followers
                </div>
              </div>
              <div className={`text-xs font-bold ${growthColor}`}>
                {growth > 0 ? "+" : ""}
                {growth.toLocaleString()}
              </div>
            </div>
            <div className="h-16 flex items-end gap-0.5 px-1">
              {candles.length === 0 ? (
                <div className="text-[10px] text-gray-600 italic w-full text-center">
                  sin snapshots
                </div>
              ) : (
                candles.map((c, i) => (
                  <div key={c.date} className="flex-1 h-full">
                    <Candle
                      delta={c.delta}
                      maxAbsDelta={maxAbs}
                      isFirst={i === 0}
                    />
                  </div>
                ))
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500">
              <span>
                {m.unlinked ? (
                  <span className="text-amber-400">⚠️ Cuenta sin modelo vinculada</span>
                ) : (
                  <>Últimos {candles.length} días</>
                )}
              </span>
              {m.modelo_id && (
                <span className="inline-flex items-center gap-0.5">
                  Ver detalle <ArrowRight className="w-3 h-3" />
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Full-screen detail modal for a single model. Big chart on top + day-by-day list below. */
export function ModelStatsDetailModal({ modeloId, modeloName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!modeloId) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/jgmpro/models/${modeloId}/stats?days=${days}`,
          { withCredentials: true }
        );
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [modeloId, days]);

  if (!modeloId) return null;

  const candles = data?.candles || [];
  const maxAbs = Math.max(1, ...candles.map((c) => Math.abs(c.delta || 0)));
  const totals = data?.totals || {};
  const growth = totals.delta_window || 0;
  const growthColor =
    growth > 0 ? "text-emerald-400" : growth < 0 ? "text-rose-400" : "text-gray-300";

  return (
    <div
      data-testid="jgmpro-model-stats-modal"
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-gradient-to-br from-zinc-900 to-zinc-950 border border-pink-500/20 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <h3 className="text-white text-xl font-bold">{modeloName}</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Estadísticas Instagram · {data?.days_window || 0} días con datos
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-2 py-1 text-sm"
            >
              <option value={7}>7 días</option>
              <option value={14}>14 días</option>
              <option value={30}>30 días</option>
              <option value={90}>90 días</option>
            </select>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-2"
              data-testid="jgmpro-model-stats-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Cargando estadísticas…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBox label="Followers actuales" value={totals.latest_followers?.toLocaleString() || "0"} />
              <StatBox
                label="Crecimiento en ventana"
                value={`${growth > 0 ? "+" : ""}${growth.toLocaleString()}`}
                valueClass={growthColor}
              />
              <StatBox label="Engagement promedio" value={`${totals.engagement_avg || 0}%`} />
              <StatBox label="Cuentas vinculadas" value={data?.accounts?.length || 0} />
            </div>

            {/* Candle chart */}
            <div className="bg-zinc-900/70 border border-white/5 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white text-sm font-semibold">
                  Crecimiento diario (velas)
                </div>
                <div className="flex items-center gap-3 text-[10px] text-gray-400">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Sube
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 bg-rose-500 rounded-sm" /> Baja
                  </span>
                </div>
              </div>
              {candles.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Aún no hay snapshots para esta modelo.
                </div>
              ) : (
                <div className="h-40 flex items-end gap-1">
                  {candles.map((c, i) => (
                    <div key={c.date} className="flex-1 h-full flex flex-col items-center">
                      <Candle delta={c.delta} maxAbsDelta={maxAbs} isFirst={i === 0} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Day-by-day list */}
            <div className="bg-zinc-900/70 border border-white/5 rounded-xl p-4">
              <div className="text-white text-sm font-semibold mb-3">
                Detalle día a día
              </div>
              {candles.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">Sin datos.</div>
              ) : (
                <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
                  {[...candles].reverse().map((c) => (
                    <div
                      key={c.date}
                      className="flex items-center justify-between py-2 text-sm"
                      data-testid={`jgmpro-day-row-${c.date}`}
                    >
                      <div>
                        <div className="text-white font-medium">
                          {new Date(c.date).toLocaleDateString("es-CO", {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                          })}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {c.followers.toLocaleString()} followers · engagement{" "}
                          {c.engagement_rate}%
                        </div>
                      </div>
                      <div
                        className={`inline-flex items-center gap-1 font-semibold text-sm ${
                          c.delta > 0
                            ? "text-emerald-400"
                            : c.delta < 0
                            ? "text-rose-400"
                            : "text-gray-400"
                        }`}
                      >
                        {c.delta > 0 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : c.delta < 0 ? (
                          <TrendingDown className="w-4 h-4" />
                        ) : null}
                        {c.delta > 0 ? "+" : ""}
                        {c.delta.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, valueClass }) {
  return (
    <div className="bg-zinc-900/70 border border-white/5 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg font-bold text-white mt-0.5 ${valueClass || ""}`}>
        {value}
      </div>
    </div>
  );
}
